/**
 * Analytics — the global, fleet-wide companion to the per-device Demo
 * experience section. Same visual vocabulary; the only difference is
 * that we don't pass a `thing` prop, so the drill-down hits the
 * fleet-wide rollup.
 *
 * Filters (DE-161): group + tenant. Both are URL-backed so links are
 * shareable. The tenant dropdown only renders for super-admin viewers
 * (the /admin/tenants endpoint that feeds it is 403 for others).
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ARTOMEUI_ENV_GROUPS,
  ENV_GROUP_LABEL,
  useDeviceGroups,
  useMe,
  useTenants,
  type AnalyticsFilters,
  type EnvGroup,
} from '../api/hooks';
import { DemoExperience } from '../components/DemoExperience';

const ALL = 'all' as const;
type GroupFilter = typeof ALL | EnvGroup;
type TenantFilter = typeof ALL | string;

function isEnvGroup(s: string | null): s is EnvGroup {
  return (
    s !== null &&
    (ARTOMEUI_ENV_GROUPS as readonly string[]).includes(s)
  );
}

const SELECT_CLASS =
  'rounded-md border border-border bg-white px-2 py-1 text-xs text-fg disabled:opacity-50';

export function Analytics() {
  const me = useMe();
  const isSuperAdmin = me.data?.role === 'super-admin';
  const groups = useDeviceGroups();
  // useTenants relies on /admin/tenants which is super-admin only; skip
  // the hook entirely for other roles so we don't fire a 403'ing request.
  const tenants = useTenants();
  const [params, setParams] = useSearchParams();

  const groupParam = params.get('group');
  const groupFilter: GroupFilter = isEnvGroup(groupParam) ? groupParam : ALL;
  const tenantParam = params.get('tenant');
  const tenantFilter: TenantFilter =
    isSuperAdmin && tenantParam ? tenantParam : ALL;

  const setGroup = (next: GroupFilter) => {
    const p = new URLSearchParams(params);
    if (next === ALL) p.delete('group');
    else p.set('group', next);
    setParams(p, { replace: true });
  };
  const setTenant = (next: TenantFilter) => {
    const p = new URLSearchParams(params);
    if (next === ALL) p.delete('tenant');
    else p.set('tenant', next);
    setParams(p, { replace: true });
  };

  // Resolve a chosen group to its member device-thing list. Groups live
  // in AWS IoT (not RDS); we fetch the cards client-side and pass the
  // resulting list as ?things=t1,t2,t3 to the analytics endpoints.
  const things = useMemo(() => {
    if (groupFilter === ALL) return undefined;
    const card = groups.data?.groups.find((g) => g.name === groupFilter);
    return card?.devices ?? [];
  }, [groupFilter, groups.data]);

  const filters: AnalyticsFilters | undefined = useMemo(() => {
    const hasThings = things !== undefined;
    const hasTenant = tenantFilter !== ALL;
    if (!hasThings && !hasTenant) return undefined;
    return {
      ...(hasThings ? { things } : {}),
      ...(hasTenant ? { tenant: tenantFilter } : {}),
    };
  }, [things, tenantFilter]);

  // When a group has zero devices the analytics queries would silently
  // return "no data". Surface that explicitly so the operator knows it's
  // the filter, not missing telemetry.
  const groupEmpty = things !== undefined && things.length === 0;

  const showTenantFilter = isSuperAdmin && (tenants.data?.tenants.length ?? 0) > 1;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-muted">
            Aggregated from the typed-event stream across the whole fleet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted">Group</label>
          <select
            value={groupFilter}
            onChange={(e) => setGroup(e.target.value as GroupFilter)}
            className={SELECT_CLASS}
            aria-label="Filter analytics by env group"
            disabled={groups.isLoading}
          >
            <option value={ALL}>All groups</option>
            {ARTOMEUI_ENV_GROUPS.map((g) => (
              <option key={g} value={g}>
                {ENV_GROUP_LABEL[g]}
              </option>
            ))}
          </select>
          {showTenantFilter && (
            <>
              <label className="text-xs text-muted ml-2">Tenant</label>
              <select
                value={tenantFilter}
                onChange={(e) => setTenant(e.target.value)}
                className={SELECT_CLASS}
                aria-label="Filter analytics by tenant"
              >
                <option value={ALL}>All tenants</option>
                {(tenants.data?.tenants ?? []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {groupEmpty && (
        <p className="card text-sm text-muted">
          The {groupFilter === ALL ? '' : ENV_GROUP_LABEL[groupFilter]} group
          has no devices. Analytics below would be empty under this filter.
        </p>
      )}

      <DemoExperience filters={filters} />
    </div>
  );
}
